import { fr } from './fr';
import { en } from './en';
import { configurerFormats } from '../lib/format';

/**
 * Les langues de l'interface.
 *
 * Le français fait foi : chaque texte naît dans `fr/<écran>.ts`, et la version
 * anglaise est TYPÉE sur la française. Une phrase oubliée en anglais ne compile
 * pas — c'est la seule garantie qui tienne quand trois personnes traduisent en
 * parallèle et qu'un écran est ajouté six mois plus tard.
 *
 * On lit les textes par `T.<écran>.<clé>`, toujours DANS le rendu ou dans une
 * fonction, jamais au niveau du module : `T` change d'objet quand on change de
 * langue, et une constante calculée au chargement garderait l'ancienne.
 */
export type Textes = typeof fr;
export type Langue = 'fr' | 'en';
export const LANGUES: { code: Langue; nom: string }[] = [
  { code: 'fr', nom: 'Français' },
  { code: 'en', nom: 'English' },
];

const DICTIONNAIRES: Record<Langue, Textes> = { fr, en };

/** Les textes de la langue active. Liaison vivante : réassignée au changement de langue. */
export let T: Textes = fr;
export let langue: Langue = 'fr';

/**
 * Ce que l'agence a réglé : sa langue par défaut, son pays (formats), sa devise.
 * Vient de /api/auth/identite, lisible avant même la connexion.
 */
export interface ReglagesRegionaux {
  locale: string;          // ex. 'fr-DZ', 'en-GB', 'en-US', 'en-AU', 'fr-FR'
  devise: string;          // ex. 'DZD', 'EUR', 'AUD'
  uniteDistance: 'km' | 'mi';
}
let reglages: ReglagesRegionaux = { locale: 'fr-DZ', devise: 'DZD', uniteDistance: 'km' };
export const reglagesRegionaux = (): ReglagesRegionaux => reglages;

const CLE = 'langue';
const lireChoix = (): Langue | null => {
  try {
    const v = localStorage.getItem(CLE);
    return v === 'fr' || v === 'en' ? v : null;
  } catch { return null; }
};

/**
 * La langue à utiliser, dans cet ordre :
 *   1. celle que la PERSONNE a choisie sur cet appareil ;
 *   2. celle de l'AGENCE (sa locale) ;
 *   3. celle du navigateur ;
 *   4. le français.
 */
export function langueInitiale(localeAgence?: string): Langue {
  const choix = lireChoix();
  if (choix) return choix;
  const agence = (localeAgence ?? '').slice(0, 2);
  if (agence === 'fr' || agence === 'en') return agence;
  const nav = (typeof navigator !== 'undefined' ? navigator.language : 'fr').slice(0, 2);
  return nav === 'en' ? 'en' : 'fr';
}

/**
 * Applique une langue et des réglages régionaux.
 *
 * La locale de l'agence garde son PAYS (dates, séparateurs) mais prend la
 * LANGUE choisie : une agence de Montréal en anglais lit « en-CA », une agence
 * d'Alger en anglais lit « en-DZ » — dates jour/mois, comme chez elle.
 */
export function appliquerLangue(l: Langue, r?: Partial<ReglagesRegionaux>): void {
  langue = l;
  T = DICTIONNAIRES[l];
  if (r) reglages = { ...reglages, ...r };
  const pays = reglages.locale.split('-')[1];
  const locale = pays ? `${l}-${pays}` : l;
  if (typeof document !== 'undefined') document.documentElement.lang = l;
  configurerFormats({
    locale, langue: l, devise: reglages.devise,
    statuts: T.commun.statuts, roles: T.commun.roles,
  });
}

/** Retenir le choix de la personne sur cet appareil. */
export function memoriserLangue(l: Langue): void {
  try { localStorage.setItem(CLE, l); } catch { /* navigation privée : on n'insiste pas */ }
}

appliquerLangue('fr');
